import { NavLink, useParams } from 'react-router-dom';
import type { LedgerType } from '@/api/types';

interface TabItem {
    label: string;
    path: string;
    assetOnly?: boolean;
}

const tabs: TabItem[] = [
    { label: 'Value', path: 'values' },
    { label: 'Cash Flow', path: 'cash-flow', assetOnly: true },
    { label: 'Analytics', path: 'analytics' },
];

interface TabNavigationProps {
    ledgerType: LedgerType;
}

/**
 * Tab navigation component for the Ledger Detail page.
 * Uses NavLink for URL-based active state management.
 * Matches existing app tab styling patterns.
 * - Hides Cash Flow tab for debt ledgers
 * - Tabs stretch to fill available width on mobile
 */
export function TabNavigation({ ledgerType }: TabNavigationProps) {
    const { id } = useParams<{ id: string }>();
    const baseUrl = `/ledgers/${id}`;

    // Filter tabs based on ledger type (Cash Flow only for assets)
    const visibleTabs = tabs.filter((tab) => !tab.assetOnly || ledgerType === 'asset');

    return (
        <nav
            className="border-b border-gray-200 dark:border-gray-700"
            aria-label="Ledger tabs"
        >
            <div className="flex">
                {visibleTabs.map((tab) => (
                    <NavLink
                        key={tab.path}
                        to={`${baseUrl}/${tab.path}`}
                        className={({ isActive }) =>
                            `flex-1 sm:flex-none py-3 px-4 sm:px-6 text-sm font-medium border-b-2 transition-colors text-center ${
                                isActive
                                    ? 'border-accent text-accent dark:border-primary-light dark:text-primary-light'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:border-gray-600'
                            }`
                        }
                    >
                        {tab.label}
                    </NavLink>
                ))}
            </div>
        </nav>
    );
}
